import type { Command, Manager } from '../lib';
import { generateId, getListOfNames, getRandomItem, NameList } from '../lib';

export interface AgentOptions {
	position: number;
	delay?: number;
	debug?: boolean;
	name?: string;
	id?: string;
	avatar?: string;
	skill?: number;
}

const nameList = getListOfNames();

export abstract class AgentBase {
	position: number;
	id: string;
	avatar: string | undefined;
	public skill: number;
	protected _isBot: boolean = false;
	private timerId!: ReturnType<typeof setTimeout>; //https://stackoverflow.com/a/56239226
	private delay: number;
	private debug: boolean;
	private _name: string;

	constructor(options: AgentOptions) {
		const { position, delay, debug, name, id, avatar, skill } = options;
		this.position = position;
		this.delay = delay ?? 1000;
		this.debug = debug ?? false;
		this._name = name ?? nameList[this.position] ?? getRandomItem(NameList);
		this.id = id ?? generateId();
		this.avatar = avatar;
		this.skill = skill ?? 0; //default skill level
	}

	get name() {
		return this._name;
	}

	get isBot() {
		return this._isBot;
	}

	public doAction(game: Manager): Command | null {
		//refetch the command in case the state has changed
		const command = this.getCommand(game);

		if (command) {
			if (this.debug) {
				console.info(`Command for player for game stage "${game.gameState.roundStage}" is`, command);
			}

			const success = game.action(command);

			if (!success) {
				if (this.debug) {
					console.error('Error, invalid command from tick', command);
				}
			}

			return command;
		}

		return null;
	}

	public async tick(game: Manager): Promise<void> {
		clearTimeout(this.timerId);
		const command = this.getCommand(game);

		if (this.debug) {
			console.info(`Command for player for game stage "${game.gameState.roundStage}" is`, command);
		}

		if (!command) {
			if (this.debug) {
				console.info('No command, not setting a timeout');
			}

			return;
		}

		this.timerId = setTimeout(async () => {
			this.doAction(game);
		}, this.delay);
	}

	/** Schedule doAction after this bot's delay, cancelling any pending action. */
	protected scheduleAction(game: Manager): void {
		clearTimeout(this.timerId);
		this.timerId = setTimeout(() => {
			this.doAction(game);
		}, this.delay);
	}

	public abstract getCommand(game: Manager): Command | null;

}
